import React, { useMemo, useState, useEffect } from 'react';
import { Fixture, Player, GameState, Result, CupFixture, TeamInLeague, TacticalPosition, TeamColors, DisplayFixture } from '../../domain/entities';
import { Modal } from './Modal';
import { MatchReportService } from '../../services/MatchReportService';
import { View } from '../../App';
import { LinkedText } from './LinkedText';
import { BalanceOfPlay } from './BalanceOfPlay';
import { calculateZoneStrengths } from '../../services/MatchAnalysisService';
import { AiService } from '../../services/AiService';
import { FORMATIONS } from '../../domain/constants';
import { getReadableAccentColor } from '../../utils/utilities';
import { PlayerFace } from './PlayerFace';
import { TeamBadge } from './TeamBadge';

interface MatchDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    fixture: DisplayFixture;
    getPlayerById: (id: number) => Player | undefined;
    gameState: GameState;
    pushView: (view: View) => void;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
}

interface TacticalSummary {
    area: 'Midfield' | 'Attack' | 'Defence';
    verdict: string;
    isPositive: boolean | null;
}

const generateTacticalSummary = (isPlayerHome: boolean, result: Result): TacticalSummary[] => {
    const playerStats = {
        possession: isPlayerHome ? result.homePossession : result.awayPossession,
        shots: isPlayerHome ? result.homeShots : result.awayShots,
        sot: isPlayerHome ? result.homeShotsOnTarget : result.awayShotsOnTarget,
        goals: isPlayerHome ? result.homeScore : result.awayScore,
    };
    const opponentStats = {
        possession: !isPlayerHome ? result.homePossession : result.awayPossession,
        shots: !isPlayerHome ? result.homeShots : result.awayShots,
        sot: !isPlayerHome ? result.homeShotsOnTarget : result.awayShotsOnTarget,
        goals: !isPlayerHome ? result.homeScore : result.awayScore,
    };

    const summaries: TacticalSummary[] = [];

    if (playerStats.possession > 60) {
        summaries.push({ area: 'Midfield', verdict: 'Dominated the midfield battle, controlling the flow of the game.', isPositive: true });
    } else if (playerStats.possession < 40) {
        summaries.push({ area: 'Midfield', verdict: 'Struggled to keep the ball and were often overrun in the middle.', isPositive: false });
    } else {
        summaries.push({ area: 'Midfield', verdict: 'It was an even contest for control of the midfield.', isPositive: null });
    }

    const conversionRate = playerStats.sot > 0 ? playerStats.goals / playerStats.sot : 0;
    if (playerStats.shots < 8) {
        summaries.push({ area: 'Attack', verdict: 'Failed to create enough clear-cut chances to threaten the opposition.', isPositive: false });
    } else if (playerStats.sot > 7 && conversionRate < 0.25) {
        summaries.push({ area: 'Attack', verdict: 'Created plenty of opportunities but were wasteful in front of goal.', isPositive: false });
    } else if (conversionRate >= 0.5 && playerStats.goals > 1) {
        summaries.push({ area: 'Attack', verdict: 'Showed a clinical edge, converting chances effectively to secure the result.', isPositive: true });
    } else {
        summaries.push({ area: 'Attack', verdict: 'A solid but unspectacular performance in the final third.', isPositive: null });
    }

    if (opponentStats.goals === 0) {
        summaries.push({ area: 'Defence', verdict: 'A rock-solid defensive display earned a well-deserved clean sheet.', isPositive: true });
    } else if (opponentStats.sot > 7) {
        summaries.push({ area: 'Defence', verdict: 'The backline was too easily breached and allowed too many shots on goal.', isPositive: false });
    } else if (opponentStats.sot < 4) {
        summaries.push({ area: 'Defence', verdict: 'A disciplined defensive effort limited the opposition to few chances.', isPositive: true });
    } else {
        summaries.push({ area: 'Defence', verdict: 'The defence held up for the most part but had some vulnerable moments.', isPositive: null });
    }

    return summaries;
};

interface PlayerRatingsTableProps {
    team: TeamInLeague;
    ratings: { [playerId: number]: number };
    scorers: number[];
    assists: { assisterId: number; goalScorerId: number }[];
    potmPlayerId: number | undefined;
    getPlayerById: (id: number) => Player | undefined;
    pushView: (view: View) => void;
    onClose: () => void;
}


const PlayerRatingsTable: React.FC<PlayerRatingsTableProps> = ({
    team, ratings, scorers, assists, potmPlayerId, getPlayerById, pushView, onClose
}) => {
    
    const handlePlayerClick = (playerId: number, playerName: string) => {
        onClose();
        pushView({ type: 'playerDetail', title: `Player: ${playerName}`, playerId });
    };

    const lineupPositions = useMemo(() => {
        const map = new Map<number, string>();
        for (const pos in team.tactics.lineup) {
            const playerId = team.tactics.lineup[pos as TacticalPosition];
            if (playerId !== null) {
                map.set(playerId, pos);
            }
        }
        return map;
    }, [team.tactics.lineup]);

    const goalCounts = useMemo(() => {
        const counts = new Map<number, number>();
        scorers.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
        return counts;
    }, [scorers]);

    const assistCounts = useMemo(() => {
        const counts = new Map<number, number>();
        assists.forEach(({ assisterId }) => counts.set(assisterId, (counts.get(assisterId) || 0) + 1));
        return counts;
    }, [assists]);

    const sortedPlayers = useMemo(() => {
        const formationPositions = FORMATIONS[team.tactics.formation] || [];

        return Object.keys(ratings)
            .map(idStr => Number(idStr))
            .map(id => getPlayerById(id))
            .filter((p): p is Player => !!p)
            .map(p => ({
                ...p,
                rating: ratings[p.id] || 0,
                tacticalPosition: lineupPositions.get(p.id) || p.position,
                goals: goalCounts.get(p.id) || 0,
                assists: assistCounts.get(p.id) || 0,
                isPotm: potmPlayerId === p.id,
            }))
            .sort((a, b) => {
                const indexA = formationPositions.indexOf(a.tacticalPosition);
                const indexB = formationPositions.indexOf(b.tacticalPosition);

                if (indexA === -1 && indexB === -1) return 0; // Keep original order if not in formation
                if (indexA === -1) return 1; // Put players not in formation at the end
                if (indexB === -1) return -1;
                
                return indexA - indexB; // Sort by formation order
            });
    }, [ratings, getPlayerById, lineupPositions, goalCounts, assistCounts, potmPlayerId, team.tactics.formation]);

    return (
        <div className="table-wrapper" style={{ maxHeight: '250px', overflowY: 'auto' }}>
            <table className="table">
                <thead>
                    <tr>
                        <th>Pos</th>
                        <th>Player</th>
                        <th style={{ textAlign: 'center' }}>Rating</th>
                        <th style={{ textAlign: 'center' }}>G</th>
                        <th style={{ textAlign: 'center' }}>A</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPlayers.map(player => (
                        <tr key={player.id}>
                            <td>{player.tacticalPosition.startsWith('ST (') ? 'ST' : player.tacticalPosition}</td>
                            <td>
                                <button className="link-style" onClick={() => handlePlayerClick(player.id, player.name)}>
                                    {player.name} {player.isPotm && '⭐'}
                                </button>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                {player.rating.toFixed(1)}
                            </td>
                            <td style={{ textAlign: 'center' }}>{player.goals > 0 ? player.goals : ''}</td>
                            <td style={{ textAlign: 'center' }}>{player.assists > 0 ? player.assists : ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const MatchDetailsModal = ({ isOpen, onClose, fixture, getPlayerById, gameState, pushView, isAiPunditEnabled, showRatingsOnMatchReport, onCacheAiMatchReport }: MatchDetailsModalProps) => {
    const [isSharing, setIsSharing] = useState(false);
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [reportError, setReportError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && isAiPunditEnabled && !fixture.result?.aiPunditReport && !isLoadingReport && !reportError) {
            const generateReport = async () => {
                setIsLoadingReport(true);
                setReportError(null);
                try {
                    const report = await AiService.generateMatchReport(gameState, fixture);
                    onCacheAiMatchReport(fixture, report);
                } catch (e) {
                    console.error("AI match report generation failed:", e);
                    setReportError("The AI Pundit failed to analyze this match.");
                } finally {
                    setIsLoadingReport(false);
                }
            };
            generateReport();
        }
    }, [isOpen, isAiPunditEnabled, fixture, onCacheAiMatchReport, gameState, isLoadingReport, reportError]);

    if (!fixture.result) return null;

    const { home, away, result } = fixture;
    const { homeScore, awayScore } = result;

    const allTeams = Object.values(gameState.leagues).flatMap(l => l.teams);
    const homeTeam = allTeams.find(t => t.name === fixture.home);
    const awayTeam = allTeams.find(t => t.name === fixture.away);
    
    const offlineReport = useMemo(() => {
        return homeTeam && awayTeam 
            ? MatchReportService.generateMatchReport(homeTeam, awayTeam, fixture as Fixture, getPlayerById)
            : "Could not find team data to generate report.";
    }, [homeTeam, awayTeam, fixture, getPlayerById]);

    const punditReport = result.aiPunditReport;
    const punditTitle = (isAiPunditEnabled && punditReport) ? punditReport.title : "Pundit's Analysis";
    const punditMessage = (isAiPunditEnabled && punditReport) ? punditReport.message : offlineReport;

    const { homeEvents, awayEvents, homePenalties, awayPenalties } = useMemo(() => {
        if (!result || !homeTeam || !awayTeam) return { homeEvents: [], awayEvents: [], homePenalties: [], awayPenalties: [] };

        const find_player_in_team = (name: string, team: TeamInLeague): Player | undefined => {
            return team.squad.find(p => p.name === name);
        };

        const homeEventsList: { minute: number; type: 'goal' | 'yellow' | 'red'; player: Player }[] = [];
        const awayEventsList: { minute: number; type: 'goal' | 'yellow' | 'red'; player: Player }[] = [];
        const homePenaltyList: { minute: number; message: string; scored: boolean; playerName: string }[] = [];
        const awayPenaltyList: { minute: number; message: string; scored: boolean; playerName: string }[] = [];

        result.events.forEach(event => {
            if (event.type === 'goal') {
                const playerName = event.message.match(/GOAL! (.*?) scores/)?.[1];
                if (playerName) {
                    const team = event.team === homeTeam.name ? homeTeam : awayTeam;
                    const player = find_player_in_team(playerName, team);
                    if (player) {
                        const list = event.team === homeTeam.name ? homeEventsList : awayEventsList;
                        list.push({ minute: event.minute, type: 'goal', player });
                    }
                }
            } else if (event.type === 'penalty_scored' || event.type === 'penalty_missed') {
                const playerName = event.message.split(' ')[0];
                const scored = event.type === 'penalty_scored';
                const penalty = { minute: event.minute, message: event.message, scored, playerName };
                if (event.team === homeTeam.name) {
                    homePenaltyList.push(penalty);
                } else {
                    awayPenaltyList.push(penalty);
                }
            }
        });

        result.yellowCards.forEach(card => {
            const player = getPlayerById(card.playerId);
            if (player) {
                const list = homeTeam.squad.some(p => p.id === player.id) ? homeEventsList : awayEventsList;
                list.push({ minute: card.minute, type: 'yellow', player });
            }
        });
        result.redCards.forEach(card => {
            const player = getPlayerById(card.playerId);
            if (player) {
                const list = homeTeam.squad.some(p => p.id === player.id) ? homeEventsList : awayEventsList;
                list.push({ minute: card.minute, type: 'red', player });
            }
        });

        homeEventsList.sort((a, b) => a.minute - b.minute);
        awayEventsList.sort((a, b) => a.minute - b.minute);

        return { homeEvents: homeEventsList, awayEvents: awayEventsList, homePenalties: homePenaltyList, awayPenalties: awayPenaltyList };

    }, [result, homeTeam, awayTeam, getPlayerById]);
    
    const homeResultClass = homeScore > awayScore ? 'score-win' : (homeScore < awayScore ? 'score-loss' : 'score-draw');
    const awayResultClass = awayScore > homeScore ? 'score-win' : (awayScore < homeScore ? 'score-loss' : 'score-draw');

    const isPlayerInvolved = home === gameState.playerTeamName || away === gameState.playerTeamName;
    const isPlayerHome = home === gameState.playerTeamName;

    const tacticalSummary = isPlayerInvolved ? generateTacticalSummary(isPlayerHome, result) : null;
    
    const handlePlayerClick = (playerId: number, playerName: string) => {
        onClose();
        pushView({ type: 'playerDetail', title: `Player: ${playerName}`, playerId });
    };
    
    const handleTeamClick = (teamName: string) => {
        onClose();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    const potmPlayer = result.potm ? getPlayerById(result.potm.playerId) : null;

    const potmTeam = useMemo(() => {
        const potmTeamName = result.potm?.teamName;
        if (!potmTeamName) return null;
        return Object.values(gameState.leagues).flatMap(l => l.teams).find(t => t.name === potmTeamName);
    }, [result.potm, gameState.leagues]);

    const readablePotmTeamColor = useMemo(() => {
        if (!potmTeam) return 'var(--text-color)';

        const cardBgColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color').trim();
        return getReadableAccentColor(potmTeam.colors.primary, cardBgColor);
    }, [potmTeam]);

    const balanceData = useMemo(() => {
        if (!homeTeam || !awayTeam || !result) return null;
        
        const homeStrengths = calculateZoneStrengths(homeTeam, result.homeStartingXI);
        const awayStrengths = calculateZoneStrengths(awayTeam, result.awayStartingXI);
        
        return {
            homeName: homeTeam.name,
            awayName: awayTeam.name,
            homeColors: homeTeam.colors,
            awayColors: awayTeam.colors,
            homeAttack: homeStrengths.attack,
            homeMid: homeStrengths.mid,
            homeDef: homeStrengths.def,
            awayAttack: awayStrengths.attack,
            awayMid: awayStrengths.mid,
            awayDef: awayStrengths.def,
        };
    }, [homeTeam, awayTeam, result]);
    
    const handleShare = async () => {
        if (!homeTeam || !awayTeam || !result) return;
        setIsSharing(true);
        try {
            await document.fonts.load("bold 1em 'Silkscreen'");
            await document.fonts.load("2em 'Silkscreen'");

            const headline = isAiPunditEnabled
                ? await AiService.generateMatchHeadline(result, homeTeam.name, awayTeam.name)
                : MatchReportService.generateOfflineHeadline(result, homeTeam.name, awayTeam.name);

            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 250;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const docStyle = getComputedStyle(document.documentElement);
            const BG_COLOR = docStyle.getPropertyValue('--background-color').trim();
            const TEXT_COLOR = docStyle.getPropertyValue('--text-color').trim();
            
            ctx.fillStyle = BG_COLOR;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Headline
            ctx.fillStyle = TEXT_COLOR;
            ctx.font = "bold 1.5em 'Silkscreen'";
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(headline, canvas.width / 2, 40);

            // Scoreline
            ctx.font = "2em 'Silkscreen'";
            const scoreText = `${result.homeScore} - ${result.awayScore}`;
            const scoreWidth = ctx.measureText(scoreText).width;
            ctx.fillText(scoreText, canvas.width / 2, 125);
            
            // Teams
            ctx.font = "1.2em 'Silkscreen'";
            ctx.fillStyle = homeTeam.colors.primary;
            ctx.textAlign = 'right';
            ctx.fillText(homeTeam.name, canvas.width / 2 - scoreWidth / 2 - 20, 125);

            ctx.fillStyle = awayTeam.colors.primary;
            ctx.textAlign = 'left';
            ctx.fillText(awayTeam.name, canvas.width / 2 + scoreWidth / 2 + 20, 125);

            // POTM
            if (potmPlayer) {
                ctx.fillStyle = TEXT_COLOR;
                ctx.font = "1em 'Silkscreen'";
                ctx.textAlign = 'center';
                ctx.fillText(`⭐ MOTM: ${potmPlayer.name}`, canvas.width / 2, 180);
            }

            // Footer
            ctx.fillStyle = TEXT_COLOR;
            ctx.globalAlpha = 0.7;
            ctx.font = "0.8em 'Silkscreen'";
            ctx.fillText('pixelsoccermanager.com', canvas.width / 2, canvas.height - 20);
            ctx.globalAlpha = 1.0;

            const link = document.createElement('a');
            link.download = `match-highlight-${homeTeam.name}-vs-${awayTeam.name}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();

        } catch (e) {
            console.error("Failed to share highlight card", e);
        } finally {
            setIsSharing(false);
        }
    };

    const MatchDetailsModalCSS = `
    .match-details-modal h2 { text-align: center; font-size: 1.5em; margin-bottom: 8px; }
    .match-details-modal .modal-subtitle { text-align: center; margin-top: -4px; margin-bottom: 16px; opacity: 0.8; font-size: 0.9em; }
    .match-details-modal .fixture-teams { grid-template-columns: 1fr auto 1fr; display: grid; gap: 8px; align-items: center; }
    .match-details-modal .fixture-teams > span, .match-details-modal .fixture-teams > button { min-width: 0; white-space: normal; word-break: break-word; overflow: hidden; }
    .match-details-modal .fixture-teams > span:first-child, .match-details-modal .fixture-teams > span:last-child { text-align: center; font-size: 0.95em; }
    @media (max-width: 600px) { .match-details-modal .fixture-teams { grid-template-columns: 1fr auto 1fr; font-size: 0.85em; } }
    .match-details-events { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
    .match-details-events ul { list-style-type: none; padding: 0; }
    .match-details-events li { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
    .event-icon.card { width: 0.7em; height: 0.9em; border: 1px solid rgba(0,0,0,0.3); border-radius: 2px; vertical-align: middle; display: inline-block; }
    .event-icon.yellow-card { background-color: #ffc107; }
    .event-icon.red-card { background-color: #ff4d4d; }
    .score-win { color: var(--positive-color); }
    .score-loss { color: var(--negative-color); }
    .score-draw { color: var(--text-color); }
    .breakdown-container { margin-top: 24px; padding-top: 16px; border-top: var(--border); }
    .breakdown-text { background-color: var(--primary-color); padding: 12px; font-size: 0.95em; line-height: 1.7; margin-bottom: 16px; }
    .tactical-summary-list { list-style: none; padding: 0; margin: 16px 0; }
    .tactical-summary-list li { display: flex; align-items: center; gap: 12px; padding: 8px; margin-bottom: 8px; background-color: var(--primary-color); border-left: 4px solid var(--highlight-color); }
    .tactical-summary-list li.positive { border-left-color: var(--positive-color); }
    .tactical-summary-list li.negative { border-left-color: var(--negative-color); }
    .summary-area-icon { font-size: 1.5em; }
    .player-ratings-container { margin-top: 24px; padding-top: 16px; border-top: var(--border); }
    .player-ratings-container h4 { text-align: center; margin-bottom: 12px; }
    .player-ratings-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .match-details-modal .potm-display { margin-top: 16px; padding: 12px; background-color: var(--primary-color); border: 1px solid var(--gold-color); animation: fadeIn 0.5s; }
    .match-details-modal .potm-header { font-size: 1.1em; font-weight: bold; color: var(--gold-color); text-transform: uppercase; margin-bottom: 8px; text-align: center; }
    .match-details-modal .potm-player { display: flex; align-items: center; justify-content: center; gap: 12px; }
    .match-details-modal .potm-player .player-face { width: 40px; height: 40px; font-size: 32px; margin: 0; }
    .match-details-modal .potm-player-info { text-align: left; }
    .match-details-modal .potm-player-info .link-style { display: inline; text-decoration: none !important; }
    .match-details-modal .potm-player-name { font-size: 1.2em; font-weight: bold; color: var(--text-color); }
    .match-details-modal .potm-player-team { font-size: 1em; opacity: 0.8; }
    .match-details-modal .potm-player-rating { display: block; opacity: 0.9; font-size: 0.9em; color: var(--text-color); }
    .match-details-modal .potm-player-info button:hover .potm-player-name, .match-details-modal .potm-player-info button:hover .potm-player-team { text-decoration: underline; }
    @media (max-width: 600px) { .player-ratings-grid { grid-template-columns: 1fr; } }
    .penalties-container { margin-top: 24px; padding-top: 16px; border-top: var(--border); }
    .penalties-container h4 { text-align: center; margin-bottom: 16px; }
    .penalties-score { display: grid; grid-template-columns: 1fr auto 1fr; gap: 16px; align-items: center; margin-bottom: 16px; padding: 12px; background-color: var(--primary-color); border-radius: 4px; }
    .penalties-score-item { text-align: center; }
    .penalties-score-item .team-name { font-weight: bold; margin-bottom: 4px; }
    .penalties-score-item .team-penalty-score { font-size: 1.3em; font-weight: bold; color: var(--accent-text-color); }
    .penalties-score-divider { text-align: center; font-weight: bold; opacity: 0.5; }
    .penalties-events { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .penalties-events ul { list-style-type: none; padding: 0; }
    .penalties-events li { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; padding: 6px; background-color: var(--primary-color); border-radius: 3px; font-size: 0.9em; }
    .penalty-icon { font-size: 1em; }
    `;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <style>{MatchDetailsModalCSS}</style>
            <div className="match-details-modal">
                <h2>Match Details</h2>
                <div className="modal-subtitle">{fixture.competition} - Week {Math.floor(fixture.week)}</div>
                <div className="fixture-teams">
                    <span><button className="link-style" onClick={() => handleTeamClick(home)}><span className={home === gameState.playerTeamName ? 'player-team-name' : ''}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{home}</span></button></span>
                    <span className="match-result">
                        <span className={homeResultClass}>{homeScore}</span> - <span className={awayResultClass}>{awayScore}</span>
                    </span>
                    <span><button className="link-style" onClick={() => handleTeamClick(away)}><span className={away === gameState.playerTeamName ? 'player-team-name' : ''}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{away}</span></button></span>
                </div>
                {potmPlayer && result.potm && (
                    <div className="potm-display">
                        <div className="potm-header">⭐ Player of the Match ⭐</div>
                        <div className="potm-player">
                            <button className="link-style" onClick={() => handlePlayerClick(potmPlayer.id, potmPlayer.name)}>
                                <PlayerFace player={potmPlayer} teamColor={potmTeam?.colors.primary} />
                            </button>
                            <div className="potm-player-info">
                                <div>
                                    <button className="link-style" onClick={() => handlePlayerClick(potmPlayer.id, potmPlayer.name)}>
                                        <span className="potm-player-name">{potmPlayer.name}</span>
                                    </button>
                                    {' '}
                                    <button className="link-style" onClick={() => handleTeamClick(result.potm!.teamName)}>
                                        <span className="potm-player-team" style={{ color: readablePotmTeamColor }}>
                                            {result.potm.teamName}
                                        </span>
                                    </button>
                                </div>
                                <span className="potm-player-rating">Rating: {result.potm.rating.toFixed(1)}</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="match-details-events">
                    <div>
                        <h4><span className={home === gameState.playerTeamName ? 'player-team-name' : ''}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{home}</span> Events</h4>
                        {homeEvents.length > 0 ? (
                            <ul>
                                {homeEvents.map((event, i) => (
                                    <li key={i}>
                                        {event.type === 'goal' && '⚽️'}
                                        {event.type === 'yellow' && <span className="event-icon card yellow-card" />}
                                        {event.type === 'red' && <span className="event-icon card red-card" />}
                                        <button className="link-style" onClick={() => handlePlayerClick(event.player.id, event.player.name)}>{event.player.name}</button> ({event.minute}')
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-secondary">None</p>
                        )}
                    </div>
                     <div>
                        <h4><span className={away === gameState.playerTeamName ? 'player-team-name' : ''}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{away}</span> Events</h4>
                        {awayEvents.length > 0 ? (
                             <ul>
                                {awayEvents.map((event, i) => (
                                    <li key={i}>
                                        {event.type === 'goal' && '⚽️'}
                                        {event.type === 'yellow' && <span className="event-icon card yellow-card" />}
                                        {event.type === 'red' && <span className="event-icon card red-card" />}
                                        <button className="link-style" onClick={() => handlePlayerClick(event.player.id, event.player.name)}>{event.player.name}</button> ({event.minute}')
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-secondary">None</p>
                        )}
                    </div>
                </div>

                {result.penalties && (homePenalties.length > 0 || awayPenalties.length > 0) && (
                    <div className="penalties-container">
                        <h4>⚽ Penalty Shootout</h4>
                        <div className="penalties-score">
                            <div className="penalties-score-item">
                                <div className="team-name">{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{home}</div>
                                <div className="team-penalty-score">{result.penalties.homeScore}</div>
                            </div>
                            <div className="penalties-score-divider">vs</div>
                            <div className="penalties-score-item">
                                <div className="team-name">{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{away}</div>
                                <div className="team-penalty-score">{result.penalties.awayScore}</div>
                            </div>
                        </div>
                        <div className="penalties-events">
                            <div>
                                <h5 style={{textAlign: 'center'}}>{homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{home}</h5>
                                {homePenalties.length > 0 ? (
                                    <ul>
                                        {homePenalties.map((penalty, i) => (
                                            <li key={i}>
                                                <span className="penalty-icon">{penalty.scored ? '✓' : '✗'}</span>
                                                <span>{penalty.playerName} {penalty.scored ? 'scored' : 'missed'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-secondary">No penalties</p>
                                )}
                            </div>
                            <div>
                                <h5 style={{textAlign: 'center'}}>{awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{away}</h5>
                                {awayPenalties.length > 0 ? (
                                    <ul>
                                        {awayPenalties.map((penalty, i) => (
                                            <li key={i}>
                                                <span className="penalty-icon">{penalty.scored ? '✓' : '✗'}</span>
                                                <span>{penalty.playerName} {penalty.scored ? 'scored' : 'missed'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-secondary">No penalties</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showRatingsOnMatchReport && homeTeam && awayTeam && (
                    <div className="player-ratings-container">
                        <h4>Player Ratings</h4>
                        <div className="player-ratings-grid">
                            <div>
                                <h5 style={{textAlign: 'center'}}>{home}</h5>
                                <PlayerRatingsTable 
                                    team={homeTeam}
                                    ratings={result.homePlayerRatings}
                                    scorers={result.homeScorers}
                                    assists={result.homeAssists}
                                    potmPlayerId={result.potm?.playerId}
                                    getPlayerById={getPlayerById}
                                    pushView={pushView}
                                    onClose={onClose}
                                />
                            </div>
                             <div>
                                <h5 style={{textAlign: 'center'}}>{away}</h5>
                                <PlayerRatingsTable 
                                    team={awayTeam}
                                    ratings={result.awayPlayerRatings}
                                    scorers={result.awayScorers}
                                    assists={result.awayAssists}
                                    potmPlayerId={result.potm?.playerId}
                                    getPlayerById={getPlayerById}
                                    pushView={pushView}
                                    onClose={onClose}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {balanceData && <BalanceOfPlay {...balanceData} />}

                <div className="breakdown-container">
                    <h4>{punditTitle}</h4>
                    {isAiPunditEnabled && isLoadingReport && <p>Pundit is thinking...</p>}
                    {isAiPunditEnabled && reportError && <p className="negative">{reportError}</p>}
                    {!(isAiPunditEnabled && isLoadingReport) && !reportError && <LinkedText text={punditMessage} gameState={gameState} pushView={pushView} onClose={onClose} as="p" style={{whiteSpace: 'pre-wrap', lineHeight: 1.6, marginTop: '16px'}} />}
                </div>

                {isPlayerInvolved && tacticalSummary && (
                <div className="breakdown-container">
                    <h4>Post-Match Analysis</h4>
                    
                    <ul className="tactical-summary-list">
                        {tacticalSummary.map(summary => (
                            <li key={summary.area} className={summary.isPositive === true ? 'positive' : summary.isPositive === false ? 'negative' : ''}>
                                <span className="summary-area-icon">
                                    {summary.area === 'Attack' ? '🎯' : summary.area === 'Midfield' ? '🧠' : '🛡️'}
                                </span>
                                <span>{summary.verdict}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                )}


                <div className="modal-actions">
                    {result.isSignificant && (
                        <button className="button" onClick={handleShare} disabled={isSharing}>
                           {isSharing ? 'Generating...' : 'Share Highlight 📸'}
                        </button>
                    )}
                    <button className="button" onClick={onClose}>Close</button>
                </div>
            </div>
        </Modal>
    );
};